import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { type NavigateOptions } from 'react-router-dom'

import { SuggestedRow } from '../SuggestedRow/SuggestedRow'
import { SCREEN_WIDTH_BREAKPOINT } from '../SuggestedOpportunitiesRow/useSuggestedOpportunitiesRow'

import { useQueryList } from '@percent/workplace-giving/common/hooks/useQueryList/useQueryList'
import { getSuggestedFundraisers } from '@percent/workplace-giving/api/fundraiser/suggestedFundraisers/suggestedFundraisers'
import {
  getAccountConfigFromAuthState,
  getCountryCodeFromAuthState,
  getPartnerFromAuthState
} from '@percent/workplace-giving/context/auth/authContextController/AuthContextController'
import { useAuth, useLogger, useWindowDimensions } from '@percent/workplace-giving/common/hooks'
import { FundraiserCard } from '@percent/workplace-giving/common/components/FundraiserCard/FundraiserCard'
import { FundraiserCardSkeleton } from '@percent/workplace-giving/common/components/FundraiserCard/FundraiserCardSkeleton'
import { useIsCaliforniaUser } from '@percent/workplace-giving/common/hooks/useIsCaliforniaUser/useIsCaliforniaUser'
import { AppRoute } from '@percent/workplace-giving/routing/AppRoute.enum'

type SuggestedFundraisersRowProps = Readonly<{
  sectionTitle?: string
  seeAllNavigateOptions?: NavigateOptions
}>

export function SuggestedFundraisersRow({ sectionTitle, seeAllNavigateOptions }: SuggestedFundraisersRowProps) {
  const { t } = useTranslation()
  const { state } = useAuth()
  const partner = getPartnerFromAuthState(state)!
  const accountConfig = getAccountConfigFromAuthState(state)
  const defaultCountry = getCountryCodeFromAuthState(state)!
  const { width: windowWidth } = useWindowDimensions()
  const { logError } = useLogger()

  const isWindowWide = windowWidth > SCREEN_WIDTH_BREAKPOINT
  const tilesAmount = isWindowWide ? 4 : 3

  const currencyCode = accountConfig?.currency ?? partner.currencyCode

  const isCaliforniaUser = useIsCaliforniaUser()

  const [params, setParams] = useState({
    pageSize: 4,
    currency: currencyCode,
    country: defaultCountry,
    ...(isCaliforniaUser && { ab488Compliant: true })
  })

  const { data, isPending, error } = useQueryList('suggestedFundraisers', params, getSuggestedFundraisers, setParams)

  const title = sectionTitle || t('workplace_giving.fundraising.suggestedFundraisers.title')

  const showSeeAllButton = data?.data && data.data.length >= 4

  if (data?.data && data.data.length === 0) {
    return null
  }

  if (isPending) {
    return (
      <SuggestedRow
        title={title}
        testId="suggested-fundraisers-row-skeleton"
        items={Array.from({ length: tilesAmount }, (_, i) => i.toString())}
        keyExtractor={key => key}
        renderItem={() => <FundraiserCardSkeleton />}
      />
    )
  }

  if (error) {
    logError(error)
  }

  return (
    <SuggestedRow
      title={title}
      testId="suggested-fundraisers-row"
      items={data?.data?.slice(0, tilesAmount) || []}
      keyExtractor={f => f.id}
      renderItem={f => <FundraiserCard key={f.id} fundraiser={f} />}
      seeAllHref={showSeeAllButton ? AppRoute.FUNDRAISERS : undefined}
      seeAllNavigateOptions={seeAllNavigateOptions}
    />
  )
}
